import React from 'react';
import {
  StorefrontIcon,
  PriceTagIcon,
  DeliveryTruckIcon,
  HandshakeIcon,
  ClipboardOrderIcon,
  MulticulturalGlobeIcon,
} from '../icons/CategoryIcons';

const TOP_PROPS = [
  { Icon: StorefrontIcon, title: 'We Know Your Customers', desc: 'With roots in the wholesale trade, we understand what ethnic and multicultural stores need on their shelves — because we come from these communities.' },
  { Icon: PriceTagIcon, title: 'Real Wholesale Prices', desc: 'No middleman markup. Competitive bulk pricing designed so your margins make sense and your customers keep coming back.' },
  { Icon: DeliveryTruckIcon, title: 'Canada-Wide Shipping', desc: 'Based in the GTA with reliable shipping across Canada. Your order packed right and delivered on time, every time.' },
];

const BOTTOM_PROPS = [
  { Icon: HandshakeIcon, title: 'Personal Service', desc: "We're a family business serving family businesses. Call us, WhatsApp us, email us — we pick up." },
  { Icon: ClipboardOrderIcon, title: 'Easy Ordering', desc: 'Simple online ordering with Interac e-Transfer payment. No complicated systems — just browse, order, and restock.' },
  { Icon: MulticulturalGlobeIcon, title: 'Multicultural Selection', desc: 'From tools and flags to natural products — we carry what African, Caribbean, Asian, and Latin American communities want.' },
];

const ValueCard: React.FC<{ Icon: React.FC<{ size?: number }>; title: string; desc: string }> = ({ Icon, title, desc }) => (
  <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 transition-all hover:shadow-lg hover:-translate-y-1">
    <div className="w-[46px] h-[46px] rounded-xl bg-gradient-to-br from-brand-green/10 to-brand-green/5 flex items-center justify-center mb-4">
      <Icon size={28} />
    </div>
    <h3 className="font-display font-bold text-base sm:text-lg text-brand-navy mb-2">{title}</h3>
    <p className="text-sm text-brand-text-muted leading-relaxed">{desc}</p>
  </div>
);

const WhyYaadShop: React.FC = () => {
  return (
    <section className="bg-brand-light-bg" aria-label="Why The Yaad Shop">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-10 py-16 sm:py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-green mb-3">Why The Yaad Shop</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-navy mb-3">Built for Your Business</h2>
          <p className="text-base text-brand-text-muted max-w-[520px] mx-auto leading-relaxed">
            We know multicultural retail because we come from it. Here's why shop owners trust us.
          </p>
        </div>

        {/* Top: Heritage photo + 3 cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Heritage Photo */}
          <div className="rounded-2xl overflow-hidden relative min-h-[320px] lg:min-h-0">
            <img
              src="/images/sections/heritage-photo.png"
              alt="Multicultural community marketplace"
              className="w-full h-full object-cover absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-brand-dark/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
              <p className="font-display font-bold text-xl sm:text-2xl text-white leading-snug">
                From community roots to Canada-wide wholesale
              </p>
              <p className="text-sm text-white/70 mt-2">Serving multicultural businesses since day one</p>
            </div>
          </div>

          {/* 3 stacked cards */}
          <div className="flex flex-col gap-4">
            {TOP_PROPS.map((item) => (
              <ValueCard key={item.title} {...item} />
            ))}
          </div>
        </div>

        {/* Bottom: 3 cards in a row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BOTTOM_PROPS.map((item) => (
            <ValueCard key={item.title} {...item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyYaadShop;
